import { WarningTypes, videoWarnings, imageWarnings, Props, objectTypes, warnings, WarningLevel } from './types'
import { isNull } from 'utilities'
import { Person, Image, Project, Brand, Product, AgreementDTO } from 'models'
import moment from 'moment'
import { ListFormat } from 'typescript'

const imageHasPortraitCrop = (image: Image) => {
  return (
    !isNull(image.portraitWidth) &&
    !isNull(image.portraitX) &&
    !isNull(image.portraitY)
  )
}

const validateImages = (images: Image[] = []): WarningTypes[] => {
  return !images?.length
    ? [imageWarnings.missingImages, imageWarnings.missingImageWithPortraitCrop]
    : images.some(imageHasPortraitCrop)
      ? []
      : [imageWarnings.missingImageWithPortraitCrop]
}

const validateGeneralUseImages = (images: Image[]): WarningTypes[] => {
  const imageHasSquareCrop = (image: Image) => {
    return (
      !isNull(image.squareWidth) &&
      !isNull(image.squareX) &&
      !isNull(image.squareY)
    )
  }
  const hasPortraitForGeneralUse = (image: Image) =>
    imageHasPortraitCrop(image) && image.portrait
  const hasSquareForGeneralUse = (image: Image) =>
    imageHasSquareCrop(image) && image.portrait

  if (!images.length) {
    return [imageWarnings.missingGeneralPortrait, imageWarnings.missingGeneralSquare]
  }

  const result = []

  !images.some(hasPortraitForGeneralUse) && result.push(imageWarnings.missingGeneralPortrait)
  !images.some(hasSquareForGeneralUse) && result.push(imageWarnings.missingGeneralSquare)

  return result
}

const validateVideo = ({ videoUrl, readyForEdit, children }: Project): WarningTypes[] => {
  return videoUrl
    ? readyForEdit
      ? []
      : [videoWarnings.videoNotProcessed]
    : !children
      ? [videoWarnings.missingVideo]
      : []
}

const validateProject = (project: Project): WarningTypes[] => {
  let result = []

  result = [...result, ...validateVideo(project)]
  result = [...result, ...validateImages(project.images)]

  return result
}

const validatePerson = (person: Person): WarningTypes[] => {
  return validateImages(person.images)
}

const validateBrand = (brand: Brand): WarningTypes[] => {
  if(brand.mainImage)
    return validateImages([brand.mainImage])
  else
    return validateImages();
}

const validateProduct = (product: Product): WarningTypes[] => {
  return [
    ...validateImages(product.images),
    ...validateGeneralUseImages(product.images)
  ]
}

const validateAgreement = (agreement: AgreementDTO, agreementCol: Props['agreementCol']): WarningTypes[] => {
  const {
    // agreementStatus,
    // startDate,
    endDate,
    budget,
    budgetRemaining
  } = agreement

  switch (agreementCol) {
    case 'remainingBudget': {
      if (!budgetRemaining) {
        return [warnings.noRemainingBudget]
      }
      if (budgetRemaining <= (budget / 10)) {
        return [warnings.lowBudget]
      }
      break
    }
    case 'period': {
      const now = moment()
      const periodEnd = moment(endDate)
      if (periodEnd.isBefore(now)) {
        return [warnings.periodExpired]
      }
      if (periodEnd.diff(now, 'days') <= 7) {
        return [warnings.periodAboutToExpire]
      }
      break
    }
    default: break
  }

  return []
}

export const validateObject = ({ object, objectType, agreementCol }: Props): WarningTypes[] => {
  switch (objectType) {
    case objectTypes.project: return validateProject(object as Project)
    case objectTypes.person: return validatePerson(object as Person)
    case objectTypes.brand: return validateBrand(object as Brand)
    case objectTypes.product: return validateProduct(object as Product)
    case objectTypes.agreement: return validateAgreement(object as AgreementDTO, agreementCol)

    default: return []
  }
}

export const getWarningLevel = (warning: WarningTypes): WarningLevel => {
  switch (warning) {
    case warnings.videoNotProcessed:
    case warnings.noRemainingBudget:
    case warnings.periodExpired:
      return WarningLevel.ERROR
    default:
      return WarningLevel.WARNING
  }
}
